
import React from 'react';
import BaseChart from './common/BaseChart';
import BarChartComponent from './common/BarChartComponent';
import { Badge } from '@/components/ui/badge';
import ChartSkeleton from '../common/ChartSkeleton';

interface OptimizedUserActionsChartProps {
  actionStats: any[];
  loading: boolean;
  exportCSV: (data: any[], filename: string) => void;
}

const OptimizedUserActionsChart = React.memo(({ 
  actionStats, 
  loading, 
  exportCSV 
}: OptimizedUserActionsChartProps) => {
  // Memoize the grouped actions processing
  const groupedActions = React.useMemo(() => {
    const actionDisplayNames: Record<string, string> = {
      'pageview': 'Page View',
      'interaction': 'User Interaction',
    };
    
    const actionGroups: Record<string, number> = {};
    const pagePrefixes: Record<string, Set<string>> = {};
    
    actionStats.forEach(item => {
      const actionType = item.action_type || '';
      let genericAction = actionType;
      let pagePath = '';
      
      if (actionType.includes(':')) {
        const [action, context] = actionType.split(':', 2);
        genericAction = action.trim();
        pagePath = context.split('/')[0]?.trim() || '';
        
        if (pagePath) {
          if (!pagePrefixes[genericAction]) {
            pagePrefixes[genericAction] = new Set();
          }
          pagePrefixes[genericAction].add(pagePath);
        }
      }
      
      if (genericAction) {
        actionGroups[genericAction] = (actionGroups[genericAction] || 0) + item.count;
      }
    });
    
    return Object.entries(actionGroups).map(([action, count]) => {
      let enhancedLabel = actionDisplayNames[action] || action;
      
      if (pagePrefixes[action] && pagePrefixes[action].size > 0) {
        const pages = Array.from(pagePrefixes[action]).slice(0, 3);
        if (pages.length === 1) {
          enhancedLabel += ` (${pages[0]})`;
        } else if (pages.length > 1) {
          enhancedLabel += ` (multiple areas)`;
        }
      }
      
      return {
        action_type: action,
        display_name: enhancedLabel,
        count,
      };
    });
  }, [actionStats]);
  
  const sortedData = React.useMemo(() => {
    return groupedActions
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [groupedActions]);

  if (loading) {
    return (
      <ChartSkeleton 
        title="User Actions"
        description="Distribution of user interactions by action type"
        height={450}
        type="bar"
      />
    );
  }
  
  return (
    <BaseChart
      title="User Actions"
      description="Distribution of user interactions by action type"
      data={sortedData}
      loading={false}
      exportCSV={exportCSV}
      exportFilename="action_stats"
      chartType="bar"
    >
      <div className="mb-4 flex justify-center">
        <Badge variant="secondary" className="text-xs">
          Similar actions are grouped together while preserving context
        </Badge>
      </div>
      <BarChartComponent
        data={sortedData}
        dataKey="count"
        nameKey="display_name"
        color="#3b82f6"
        formatter={(value, name) => [
          value.toString(), 
          sortedData.find(item => item.display_name === name)?.action_type || name
        ]}
        angle={-45}
        enableMultilineLabels={true}
        margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
      />
    </BaseChart>
  );
});

OptimizedUserActionsChart.displayName = 'OptimizedUserActionsChart';

export default OptimizedUserActionsChart;
